"use client";
import React, { useState, useEffect } from 'react';
import { trackEngagement } from '@/lib/analytics';

interface ReadingProgressProps {
  className?: string;
}

export default function ReadingProgress({ className = '' }: ReadingProgressProps) {
  const [progress, setProgress] = useState(0);
  const [milestoneTracked, setMilestoneTracked] = useState({
    quarter: false,
    half: false,
    threeQuarter: false,
    complete: false
  });

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      
      // Find the share section or use a fallback calculation
      const shareSection = document.querySelector('[data-share-section]') || 
                          document.querySelector('.share-section') ||
                          document.querySelector('h3:contains("Share this article")') ||
                          // Fallback: look for the share buttons container
                          document.querySelector('.flex.space-x-4');
      
      let targetScrollHeight;
      
      if (shareSection) {
        // Calculate scroll position to reach the share section
        const shareSectionTop = shareSection.getBoundingClientRect().top + window.scrollY;
        const viewportHeight = window.innerHeight;
        
        // Target is when share section is in the middle of viewport
        targetScrollHeight = Math.max(shareSectionTop - (viewportHeight / 2), 0);
      } else {
        // Fallback: use 80% of total document height to avoid footer
        const totalDocHeight = document.documentElement.scrollHeight - window.innerHeight;
        targetScrollHeight = totalDocHeight * 0.8;
      }
      
      // Prevent division by zero
      if (targetScrollHeight <= 0) {
        setProgress(0);
        return;
      }
      
      // Calculate progress as percentage to target
      const scrollPercent = Math.min((scrollTop / targetScrollHeight) * 100, 100);
      setProgress(scrollPercent);

      // Track reading milestones (only once per session)
      if (scrollPercent >= 25 && !milestoneTracked.quarter) {
        trackEngagement('reading_progress_25', 1);
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'reading_progress', {
            event_category: 'Content Engagement',
            event_label: '25% Read',
            value: 25
          });
        }
        setMilestoneTracked(prev => ({ ...prev, quarter: true }));
      }
      
      if (scrollPercent >= 50 && !milestoneTracked.half) {
        trackEngagement('reading_progress_50', 1);
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'reading_progress', {
            event_category: 'Content Engagement',
            event_label: '50% Read',
            value: 50
          });
        }
        setMilestoneTracked(prev => ({ ...prev, half: true }));
      }
      
      if (scrollPercent >= 75 && !milestoneTracked.threeQuarter) {
        trackEngagement('reading_progress_75', 1);
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'reading_progress', {
            event_category: 'Content Engagement',
            event_label: '75% Read',
            value: 75
          });
        }
        setMilestoneTracked(prev => ({ ...prev, threeQuarter: true }));
      }
      
      if (scrollPercent >= 90 && !milestoneTracked.complete) {
        trackEngagement('reading_progress_complete', 1);
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'reading_progress', {
            event_category: 'Content Engagement',
            event_label: 'Article Complete',
            value: 100
          });
        }
        setMilestoneTracked(prev => ({ ...prev, complete: true }));
      }
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Add a small delay to ensure DOM is fully loaded
    const initializeProgress = () => {
      setTimeout(() => {
        updateProgress();
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });
    
    // Initial calculation after DOM is ready
    if (document.readyState === 'complete') {
      initializeProgress();
    } else {
      window.addEventListener('load', initializeProgress);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateProgress);
      window.removeEventListener('load', initializeProgress);
    };
  }, [milestoneTracked]);

  // Only render if there's meaningful progress to show
  if (progress < 1) return null;

  return (
    <div 
      className={`fixed top-0 left-0 w-full h-1 bg-gray-200 z-50 ${className}`}
      style={{ position: 'fixed', top: 0, left: 0, right: 0 }}
    >
      <div 
        className="h-full bg-gradient-to-r from-[#00A4BD] to-[#008CA1] transition-all duration-150 ease-out"
        style={{ 
          width: `${progress}%`,
          height: '100%',
          transition: 'width 0.15s ease-out'
        }}
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Reading progress: ${Math.round(progress)}% complete`}
      />
    </div>
  );
}